import { useEffect, useRef, useState } from 'react';
import { useReveal } from '../lib/anim/useReveal';

const INTAKE_CSS = `
@keyframes intake-shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(400%); }
}
.intake-status-pill {
  display: inline-flex; align-items: center;
  padding: 3px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 500;
  transition: background .4s ease, color .4s ease;
}
.intake-status-pill.retrieving {
  background: rgba(11, 18, 32, 0.05);
  color: var(--color-mute);
  border: 1px solid var(--color-hairline);
}
.intake-status-pill.reasoning {
  background: var(--color-accent-soft);
  color: var(--color-accent);
  border: 1px solid rgba(79, 70, 229, 0.3);
}
.intake-status-pill.done {
  background: rgba(11, 18, 32, 0.05);
  color: var(--color-ink);
  border: 1px solid var(--color-hairline);
}
.intake-response-body {
  opacity: 0;
  transform: translateY(8px);
  transition: opacity .5s ease, transform .5s ease;
}
.intake-response-body.visible {
  opacity: 1;
  transform: translateY(0);
}
.intake-kb-chip {
  display: inline-flex; align-items: center;
  padding: 2px 8px; border-radius: 4px;
  font-size: 11px; font-weight: 500;
  background: var(--color-accent-soft);
  color: var(--color-accent);
  border: 1px solid rgba(79, 70, 229, 0.2);
  opacity: 0;
  transform: translateY(4px);
  transition: opacity .3s ease, transform .3s ease;
}
.intake-kb-chip.visible {
  opacity: 1;
  transform: translateY(0);
}
@media (prefers-reduced-motion: reduce) {
  .intake-response-body, .intake-kb-chip {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
  }
}
`;

let intakeCSSInjected = false;
function injectIntakeCSS() {
  if (intakeCSSInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = INTAKE_CSS;
  document.head.appendChild(style);
  intakeCSSInjected = true;
}

type StatusPhase = 'idle' | 'retrieving' | 'reasoning' | 'done';

const wait = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

export function MockupIntake() {
  const sectionHeading = useReveal();
  const bodyReveal = useReveal({ delay: 1 });
  const mockupReveal = useReveal({ delay: 2 });

  const stageRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<StatusPhase>('idle');
  const [chip1, setChip1] = useState(false);
  const [chip2, setChip2] = useState(false);
  const [chip3, setChip3] = useState(false);
  const [bodyVisible, setBodyVisible] = useState(false);
  const hasPlayed = useRef(false);

  useEffect(() => {
    injectIntakeCSS();
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      setStatus('done');
      setChip1(true); setChip2(true); setChip3(true);
      setBodyVisible(true);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      playSequence();
      return;
    }

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasPlayed.current) {
          hasPlayed.current = true;
          io.unobserve(entry.target);
          playSequence();
        }
      },
      { threshold: 0.3 }
    );
    io.observe(el);

    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function playSequence() {
    await wait(400);
    setStatus('retrieving');
    await wait(1100);
    setStatus('reasoning');
    await wait(900);
    setBodyVisible(true);
    setChip1(true);
    await wait(220);
    setChip2(true);
    await wait(220);
    setChip3(true);
    await wait(300);
    setStatus('done');
  }

  const statusLabel: Record<StatusPhase, string> = {
    idle: 'Waiting…',
    retrieving: 'Retrieving KB…',
    reasoning: 'Reasoning…',
    done: 'Done',
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-hairline)',
    borderRadius: '20px',
    padding: '24px',
  };

  const fieldStyle: React.CSSProperties = {
    background: 'var(--color-bg)',
    border: '1px solid var(--color-hairline)',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '13px',
    color: 'var(--color-ink)',
    fontFamily: 'var(--font-body)',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--color-mute)',
    fontFamily: 'var(--font-body)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: '4px',
    display: 'block',
  };

  return (
    <section
      className="py-24"
      style={{ background: 'var(--color-bg)', borderTop: '1px solid var(--color-hairline)' }}
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Section heading */}
        <div
          ref={sectionHeading.ref}
          {...sectionHeading.props}
          className={`mb-4${sectionHeading.props.className ? ' ' + sectionHeading.props.className : ''}`}
        >
          <span id="thread-start" aria-hidden />
          <p className="eyebrow" data-r>
            <span className="eyebrow-dash" />
            <span>01 · INTAKE</span>
          </p>
          <h2
            className="text-2xl md:text-3xl font-bold max-w-xl"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--color-ink)' }}
          >
            Tell the AI-CTO your vertical. It picks the engine.
          </h2>
        </div>

        <div
          ref={bodyReveal.ref}
          {...bodyReveal.props}
          className={`mb-10${bodyReveal.props.className ? ' ' + bodyReveal.props.className : ''}`}
        >
          <p
            className="text-base max-w-lg"
            style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-body)' }}
          >
            Five minutes. Structured form, RAG-grounded reasoning, output you can act on.
          </p>
        </div>

        {/* Mockup */}
        <div
          ref={(el) => {
            (mockupReveal.ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
            (stageRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }}
          {...mockupReveal.props}
          className={`grid grid-cols-1 md:grid-cols-2 gap-6${mockupReveal.props.className ? ' ' + mockupReveal.props.className : ''}`}
        >
          {/* Left: form panel */}
          <div style={cardStyle}>
            <p
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--color-mute)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                marginBottom: '16px',
                fontFamily: 'var(--font-mono)',
              }}
            >
              Intake form
            </p>

            <div className="space-y-4">
              <div>
                <label style={labelStyle}>Vertical</label>
                <div style={fieldStyle}>Power BI audits</div>
              </div>
              <div>
                <label style={labelStyle}>ICP</label>
                <div style={fieldStyle}>BI engineers inheriting models</div>
              </div>
              <div>
                <label style={labelStyle}>Input format</label>
                <div style={fieldStyle}>PBIX file</div>
              </div>
              <div>
                <label style={labelStyle}>Output format</label>
                <div style={fieldStyle}>Audit PDF</div>
              </div>
            </div>

            <button
              type="button"
              className="mt-5 w-full py-2.5 text-sm font-semibold transition-opacity hover:opacity-85"
              style={{
                background: 'var(--color-accent)',
                color: '#FFFFFF',
                borderRadius: '6px',
                fontFamily: 'var(--font-body)',
                border: 'none',
                cursor: 'default',
              }}
            >
              Submit to AI-CTO
            </button>
          </div>

          {/* Right: response card */}
          <div
            style={{
              ...cardStyle,
              background: 'var(--color-bg)',
            }}
          >
            <div
              className="flex items-center justify-between mb-4"
              style={{
                paddingBottom: '12px',
                borderBottom: '1px solid var(--color-hairline)',
              }}
            >
              <p
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--color-mute)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontFamily: 'var(--font-mono)',
                }}
              >
                AI-CTO recommendation
              </p>
              <span className={`intake-status-pill ${status === 'idle' ? 'retrieving' : status}`}>
                {statusLabel[status]}
              </span>
            </div>

            {/* KB citation chips */}
            <div className="flex gap-2 flex-wrap mb-4">
              <span className={`intake-kb-chip font-mono${chip1 ? ' visible' : ''}`}>wf-301</span>
              <span className={`intake-kb-chip font-mono${chip2 ? ' visible' : ''}`}>mm-104</span>
              <span className={`intake-kb-chip font-mono${chip3 ? ' visible' : ''}`}>ex-103</span>
            </div>

            {/* Response body */}
            <div className={`intake-response-body${bodyVisible ? ' visible' : ''}`}>
              <div
                style={{
                  borderLeft: '3px solid var(--color-accent)',
                  paddingLeft: '12px',
                  marginBottom: '12px',
                }}
              >
                <p style={{ fontSize: '12px', color: 'var(--color-mute)', fontFamily: 'var(--font-body)', marginBottom: '2px' }}>
                  Engine
                </p>
                <p
                  style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-ink)', fontFamily: 'var(--font-body)' }}
                >
                  Nexlytics Scorer
                </p>
              </div>

              {/* Suggested wrap mini-table */}
              <div
                style={{
                  background: 'var(--color-surface)',
                  borderRadius: '10px',
                  padding: '12px',
                  border: '1px solid var(--color-hairline)',
                }}
              >
                {[
                  ['input_format', 'pbix'],
                  ['output_format', 'pdf'],
                  ['price_per_run', '$9'],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex justify-between"
                    style={{
                      padding: '5px 0',
                      borderBottom: '1px solid var(--color-hairline)',
                      fontSize: '12px',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    <span style={{ color: 'var(--color-mute)' }}>{k}</span>
                    <span style={{ color: 'var(--color-ink)', fontWeight: 500 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
